import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';

import type {
  AcaoDoDestino,
  CentralWebhooksState,
  DestinoDoWebhook,
  ErroDaCentral,
  FiltroDosEventos,
} from '../types/centralWebhooksTypes';
import {
  ativarDestinoDoWebhook,
  criarIntegracaoDeWebhook,
  editarDestinoDoWebhook,
  enviarTesteDoWebhook,
  lerCentralDeWebhooks,
  lerEventoDoWebhook,
  listarEventosDoWebhook,
  listarHistoricoDoWebhook,
  pausarDestinoDoWebhook,
  reenviarEventosDoWebhook,
  retomarDestinoDoWebhook,
  salvarEventosDoWebhook,
  segredoConcluido,
  segredoIniciado,
  segredoRecusado,
} from './actions';

const FILTRO_INICIAL: FiltroDosEventos = { estado: 'TODOS', ambiente: null };

const initialState: CentralWebhooksState = {
  integracao: {
    carregadoPara: null,
    pedidoVigente: null,
    carregando: false,
    erro: null,
    dados: null,
    catalogo: null,
  },
  acaoEmCurso: null,
  falhaDaAcao: null,
  ultimoTeste: { HOMOLOGACAO: null, PRODUCAO: null },
  ultimaRetomada: { HOMOLOGACAO: null, PRODUCAO: null },
  eventos: {
    carregadoPara: null,
    filtro: FILTRO_INICIAL,
    itens: [],
    proximoCursor: null,
    pedidoVigente: null,
    carregando: false,
    erro: null,
  },
  detalhe: { eventoId: null, pedidoVigente: null, carregando: false, erro: null, dados: null },
  reenvio: { alvo: null, pedidoVigente: null, enviando: false, erro: null, desfecho: null },
  historico: { carregadoPara: null, pedidoVigente: null, carregando: false, erro: null, itens: [] },
};

const ERRO_SEM_DETALHE: ErroDaCentral = {
  status: 0,
  codigo: null,
  mensagem: 'Não foi possível concluir a operação. Tente novamente.',
};

const mesmoFiltro = (a: FiltroDosEventos, b: FiltroDosEventos) =>
  a.estado === b.estado && a.ambiente === b.ambiente;

/** O desfecho do reenvio de um evento vale só enquanto o detalhe dele está aberto. */
function descartarReenvioDoEvento(state: CentralWebhooksState, mantido: string | null) {
  const alvo = state.reenvio.alvo;
  if (alvo?.tipo === 'evento' && alvo.eventoId !== mantido) state.reenvio = initialState.reenvio;
}

/**
 * A resposta de uma mutação pertence ao escritório que a pediu. Se ela chega depois da troca de
 * escritório, aplicá-la mostraria em B o teste, o destino ou a falha de A. A leitura e o reenvio
 * já se protegem pelo `requestId`; as mutações, por esta guarda, antes de qualquer escrita.
 */
const deOutroEscritorio = (state: CentralWebhooksState, escritorioId: string) =>
  state.integracao.carregadoPara !== escritorioId;

function trocarDestino(state: CentralWebhooksState, destino: DestinoDoWebhook) {
  const integracao = state.integracao.dados;
  if (!integracao) return;
  integracao.destinos = integracao.destinos.map((atual) =>
    atual.ambiente === destino.ambiente ? destino : atual,
  );
}

/** Ações de um endereço que devolvem só o `Destino` atualizado. */
const ACOES_QUE_DEVOLVEM_O_DESTINO = [
  [editarDestinoDoWebhook, 'editar'],
  [ativarDestinoDoWebhook, 'ativar'],
  [pausarDestinoDoWebhook, 'pausar'],
] as const satisfies readonly (readonly [unknown, AcaoDoDestino])[];

const centralWebhooksSlice = createSlice({
  name: 'centralWebhooks',
  initialState,
  reducers: {
    limparFalhaDaAcao: (state) => {
      state.falhaDaAcao = null;
    },
    descartarResultadoDoTeste: (state, action: PayloadAction<DestinoDoWebhook['ambiente']>) => {
      state.ultimoTeste[action.payload] = null;
    },
    descartarResultadoDaRetomada: (state, action: PayloadAction<DestinoDoWebhook['ambiente']>) => {
      state.ultimaRetomada[action.payload] = null;
    },
    fecharDetalheDoEvento: (state) => {
      state.detalhe = initialState.detalhe;
      descartarReenvioDoEvento(state, null);
    },
    descartarResultadoDoReenvio: (state) => {
      state.reenvio = initialState.reenvio;
    },
  },
  extraReducers: (builder) => {
    // `addCase` antes de qualquer `addMatcher`: o RTK recusa a ordem inversa.
    for (const [thunk, acao] of ACOES_QUE_DEVOLVEM_O_DESTINO) {
      builder
        .addCase(thunk.pending, (state, action) => {
          state.acaoEmCurso = { acao, ambiente: action.meta.arg.ambiente };
          state.falhaDaAcao = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          if (deOutroEscritorio(state, action.meta.arg.escritorioId)) return;
          state.acaoEmCurso = null;
          trocarDestino(state, action.payload);
        })
        .addCase(thunk.rejected, (state, action) => {
          if (deOutroEscritorio(state, action.meta.arg.escritorioId)) return;
          state.acaoEmCurso = null;
          state.falhaDaAcao = {
            acao,
            ambiente: action.meta.arg.ambiente,
            erro: action.payload ?? ERRO_SEM_DETALHE,
          };
        });
    }

    builder
      .addCase(lerCentralDeWebhooks.pending, (state, action) => {
        const integracao = state.integracao;
        integracao.pedidoVigente = action.meta.requestId;
        integracao.carregando = true;
        integracao.erro = null;
        // Outro escritório: nada do anterior pode ficar à vista enquanto o novo carrega.
        if (integracao.carregadoPara !== action.meta.arg) {
          state.integracao = { ...initialState.integracao, pedidoVigente: action.meta.requestId };
          state.integracao.carregando = true;
          state.ultimoTeste = initialState.ultimoTeste;
          state.ultimaRetomada = initialState.ultimaRetomada;
          // A mutação do anterior que ainda não voltou é descartada; sem isso, os botões do novo
          // ficariam presos no "em curso" dela.
          state.acaoEmCurso = null;
          state.falhaDaAcao = null;
          // Sem pedido vigente, o 202 que chegar atrasado do escritório anterior é descartado.
          state.reenvio = initialState.reenvio;
        }
      })
      .addCase(lerCentralDeWebhooks.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.integracao.pedidoVigente) return;
        state.integracao.dados = action.payload.integracao;
        state.integracao.catalogo = action.payload.catalogo;
        state.integracao.carregadoPara = action.payload.escritorioId;
        state.integracao.carregando = false;
      })
      .addCase(lerCentralDeWebhooks.rejected, (state, action) => {
        if (action.meta.requestId !== state.integracao.pedidoVigente) return;
        state.integracao.carregando = false;
        state.integracao.erro = action.payload ?? ERRO_SEM_DETALHE;
      })
      .addCase(criarIntegracaoDeWebhook.pending, (state) => {
        state.acaoEmCurso = { acao: 'criar', ambiente: null };
        state.falhaDaAcao = null;
      })
      .addCase(salvarEventosDoWebhook.pending, (state) => {
        state.acaoEmCurso = { acao: 'salvarEventos', ambiente: null };
        state.falhaDaAcao = null;
      })
      .addCase(criarIntegracaoDeWebhook.rejected, (state, action) => {
        if (deOutroEscritorio(state, action.meta.arg.escritorioId)) return;
        state.acaoEmCurso = null;
        state.falhaDaAcao = {
          acao: 'criar',
          ambiente: null,
          erro: action.payload ?? ERRO_SEM_DETALHE,
        };
      })
      .addCase(salvarEventosDoWebhook.rejected, (state, action) => {
        if (deOutroEscritorio(state, action.meta.arg.escritorioId)) return;
        state.acaoEmCurso = null;
        state.falhaDaAcao = {
          acao: 'salvarEventos',
          ambiente: null,
          erro: action.payload ?? ERRO_SEM_DETALHE,
        };
      })
      .addCase(enviarTesteDoWebhook.pending, (state, action) => {
        state.acaoEmCurso = { acao: 'teste', ambiente: action.meta.arg.ambiente };
        state.falhaDaAcao = null;
        state.ultimoTeste[action.meta.arg.ambiente] = null;
      })
      .addCase(enviarTesteDoWebhook.fulfilled, (state, action) => {
        if (deOutroEscritorio(state, action.meta.arg.escritorioId)) return;
        state.acaoEmCurso = null;
        state.ultimoTeste[action.meta.arg.ambiente] = action.payload;
      })
      .addCase(enviarTesteDoWebhook.rejected, (state, action) => {
        if (deOutroEscritorio(state, action.meta.arg.escritorioId)) return;
        state.acaoEmCurso = null;
        state.falhaDaAcao = {
          acao: 'teste',
          ambiente: action.meta.arg.ambiente,
          erro: action.payload ?? ERRO_SEM_DETALHE,
        };
      })
      .addCase(retomarDestinoDoWebhook.pending, (state, action) => {
        state.acaoEmCurso = { acao: 'retomar', ambiente: action.meta.arg.ambiente };
        state.falhaDaAcao = null;
      })
      .addCase(retomarDestinoDoWebhook.fulfilled, (state, action) => {
        if (deOutroEscritorio(state, action.meta.arg.escritorioId)) return;
        state.acaoEmCurso = null;
        trocarDestino(state, action.payload.destino);
        state.ultimaRetomada[action.meta.arg.ambiente] = {
          reenfileirados: action.payload.reenfileirados,
          expirados: action.payload.expirados,
        };
      })
      .addCase(retomarDestinoDoWebhook.rejected, (state, action) => {
        if (deOutroEscritorio(state, action.meta.arg.escritorioId)) return;
        state.acaoEmCurso = null;
        state.falhaDaAcao = {
          acao: 'retomar',
          ambiente: action.meta.arg.ambiente,
          erro: action.payload ?? ERRO_SEM_DETALHE,
        };
      })
      .addCase(segredoIniciado, (state, action) => {
        state.acaoEmCurso = { acao: 'segredo', ambiente: action.payload };
        state.falhaDaAcao = null;
      })
      .addCase(segredoConcluido, (state, action) => {
        if (deOutroEscritorio(state, action.payload.escritorioId)) return;
        state.acaoEmCurso = null;
        trocarDestino(state, action.payload.destino);
      })
      .addCase(segredoRecusado, (state, action) => {
        const { escritorioId, ambiente, erro } = action.payload;
        if (deOutroEscritorio(state, escritorioId)) return;
        state.acaoEmCurso = null;
        state.falhaDaAcao = { acao: 'segredo', ambiente, erro };
      })
      .addCase(listarEventosDoWebhook.pending, (state, action) => {
        const { escritorioId, filtro, cursor } = action.meta.arg;
        const eventos = state.eventos;
        eventos.pedidoVigente = action.meta.requestId;
        eventos.carregando = true;
        eventos.erro = null;
        // Primeira página de outro escritório ou outro filtro: a lista anterior não vale mais.
        const outraLista =
          eventos.carregadoPara !== escritorioId || !mesmoFiltro(eventos.filtro, filtro);
        if (cursor === null && outraLista) {
          eventos.itens = [];
          eventos.proximoCursor = null;
          eventos.carregadoPara = null;
        }
        eventos.filtro = filtro;
      })
      .addCase(listarEventosDoWebhook.fulfilled, (state, action) => {
        const eventos = state.eventos;
        if (action.meta.requestId !== eventos.pedidoVigente) return;
        eventos.itens =
          action.meta.arg.cursor === null
            ? action.payload.itens
            : [...eventos.itens, ...action.payload.itens];
        eventos.proximoCursor = action.payload.proximoCursor;
        eventos.carregadoPara = action.meta.arg.escritorioId;
        eventos.carregando = false;
      })
      .addCase(listarEventosDoWebhook.rejected, (state, action) => {
        if (action.meta.requestId !== state.eventos.pedidoVigente) return;
        state.eventos.carregando = false;
        state.eventos.erro = action.payload ?? ERRO_SEM_DETALHE;
      })
      .addCase(lerEventoDoWebhook.pending, (state, action) => {
        // Reler o mesmo evento ("Atualizar") mantém o aviso: a tentativa pode ainda não ter saído.
        descartarReenvioDoEvento(state, action.meta.arg.eventoId);
        state.detalhe = {
          eventoId: action.meta.arg.eventoId,
          pedidoVigente: action.meta.requestId,
          carregando: true,
          erro: null,
          dados: null,
        };
      })
      .addCase(lerEventoDoWebhook.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.detalhe.pedidoVigente) return;
        state.detalhe.dados = action.payload;
        state.detalhe.carregando = false;
      })
      .addCase(lerEventoDoWebhook.rejected, (state, action) => {
        if (action.meta.requestId !== state.detalhe.pedidoVigente) return;
        state.detalhe.carregando = false;
        state.detalhe.erro = action.payload ?? ERRO_SEM_DETALHE;
      })
      .addCase(reenviarEventosDoWebhook.pending, (state, action) => {
        state.reenvio = {
          alvo: action.meta.arg.alvo,
          pedidoVigente: action.meta.requestId,
          enviando: true,
          erro: null,
          desfecho: null,
        };
      })
      .addCase(reenviarEventosDoWebhook.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.reenvio.pedidoVigente) return;
        state.reenvio.enviando = false;
        state.reenvio.desfecho = action.payload;
      })
      .addCase(reenviarEventosDoWebhook.rejected, (state, action) => {
        if (action.meta.requestId !== state.reenvio.pedidoVigente) return;
        state.reenvio.enviando = false;
        state.reenvio.erro = action.payload ?? ERRO_SEM_DETALHE;
      })
      .addCase(listarHistoricoDoWebhook.pending, (state, action) => {
        const historico = state.historico;
        historico.pedidoVigente = action.meta.requestId;
        historico.carregando = true;
        historico.erro = null;
        if (historico.carregadoPara !== action.meta.arg) {
          historico.itens = [];
          historico.carregadoPara = null;
        }
      })
      .addCase(listarHistoricoDoWebhook.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.historico.pedidoVigente) return;
        state.historico.itens = action.payload;
        state.historico.carregadoPara = action.meta.arg;
        state.historico.carregando = false;
      })
      .addCase(listarHistoricoDoWebhook.rejected, (state, action) => {
        if (action.meta.requestId !== state.historico.pedidoVigente) return;
        state.historico.carregando = false;
        state.historico.erro = action.payload ?? ERRO_SEM_DETALHE;
      })
      .addMatcher(
        isAnyOf(criarIntegracaoDeWebhook.fulfilled, salvarEventosDoWebhook.fulfilled),
        (state, action) => {
          if (deOutroEscritorio(state, action.meta.arg.escritorioId)) return;
          state.acaoEmCurso = null;
          state.integracao.dados = action.payload;
        },
      );
  },
});

export const {
  limparFalhaDaAcao,
  descartarResultadoDoTeste,
  descartarResultadoDaRetomada,
  fecharDetalheDoEvento,
  descartarResultadoDoReenvio,
} = centralWebhooksSlice.actions;
export const centralWebhooksReducer = centralWebhooksSlice.reducer;
